import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators, AbstractControl } from '@angular/forms';
import { AddressDetail, AddressType } from 'src/app/shared/models/address-detail';
import { AddressValidationService } from 'src/app/services/address-validation.service';
import { Address, CompanyNameAndId, CompanyType, IdentityWebApiClient, ListCompaniesByUserEmailDomainRequest, Position, RegisterUserDetailsRequest } from 'identity-webapi-client';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AuthService } from 'src/app/services';

@Component({
    selector: 'app-user-details',
    templateUrl: './user-details.component.html',
    styleUrls: ['./user-details.component.scss']
})
export class UserDetailsComponent implements OnInit {
    formGroup: FormGroup = Object.create(null);
    allPositions: string[] = ['Owner', 'Director', 'Manager'];
    yourPositions: string[] = [];
    companyTypes: string[] = ['Builder', 'Manufacturer', 'Architecture'];
    companyType: string | undefined;
    addressType = AddressType.any;
    userId: string | undefined;
    companies: CompanyNameAndId[] | undefined = [];

    get isCompanyListPopulated(): boolean { return !!this.companies?.length }

    private _showCompanyList: boolean = false;
    get showCompanyList(): boolean {
        return this._showCompanyList;
    }
    set showCompanyList(value: boolean) {
        this._showCompanyList = this.isCompanyListPopulated ? value : false
        this.assignValidators(this._showCompanyList)
    }

    get companyListMessage(): string {
        return this.showCompanyList ? "My company doesn't appear in this list" : "Choose from a list of companies"
    }

    get controls(): { [key: string]: AbstractControl; } {
        return (!this?.formGroup?.controls) ? {} : this.formGroup.controls;
    }

    constructor(
        private formBuilder: FormBuilder,
        private router: Router,
        private addressValidationService: AddressValidationService,
        private identityWebApiClient: IdentityWebApiClient,
        private snackBar: MatSnackBar,
        private authService: AuthService) { }

    ngOnInit(): void {
        this.userId = this.authService.getUserId();
        this.formGroup = this.formBuilder.group({
            firstName: [null, Validators.compose([Validators.required])],
            lastName: [null, Validators.compose([Validators.required])],
            mobile: [null, Validators.compose([Validators.required])],
            yourPosition: [null, Validators.compose([Validators.required])],
            postalAddress: [null, Validators.compose([Validators.required])],
            companyName: [null, Validators.compose([])],
            companyType: [null, Validators.compose([])],
            companies: [null, Validators.compose([])],
            sameAsPostal: [null, Validators.compose([])],
            website: [null, Validators.compose([])],
        });

        this.identityWebApiClient.listCompaniesByEmailDomain({} as ListCompaniesByUserEmailDomainRequest)
            .then(response => {
                if (response.success) {
                    this.companies = response.companies;
                    if (!!response.defaultCompany) {
                        this.controls.companies.setValue(response.defaultCompany.id)
                    }
                    if (this.isCompanyListPopulated) {
                        this.showCompanyList = true;
                    }
                }
            })
        this.controls.sameAsPostal.setValue(true);
    }

    assignValidators(showCompanyList: boolean) {
        let required = showCompanyList ? [] : [Validators.required]

        this.controls.postalAddress.setValidators(required);
        this.controls.companyName.setValidators(required);
        this.controls.companyType.setValidators(required);

        this.controls.postalAddress.updateValueAndValidity();
        this.controls.companyName.updateValueAndValidity();
        this.controls.companyType.updateValueAndValidity();
    }

    onSubmit(): void {
        const registerUserDetailsReq = {
            userId: this.userId,
            firstName: this.controls.firstName?.value,
            lastName: this.controls.lastName?.value,
            mobile: this.controls.mobile?.value,
            position: (<any>Position)[this.controls.yourPosition?.value],
            companyName: this.controls.companyName?.value,
            companyId: this.controls.companies?.value,
            companyType: (<any>CompanyType)[this.controls.companyType?.value],
            companyWebsite: this.controls.website?.value,
            postalAddress: this.addressDetailAsAddress(this.controls.postalAddress?.value),
            streetAddress: this.addressDetailAsAddress(this.controls.sameAsPostal.value ? this.controls.postalAddress?.value : this.controls.streetAddress?.value),
        } as RegisterUserDetailsRequest;

        this.identityWebApiClient.registerUserDetails(registerUserDetailsReq)
            .then(response => {
                if (response.success) {
                    this.authService.signInSilent(); //refresh token after successful adding of Company
                    this.router.navigate(['/']);
                }
                else {
                    this.snackBar.open(`There was an issue setting your company details.`, 'OK', { duration: 2000 });
                }
            })
            .catch(err => {
                console.log(err)
            });
    }

    companyListAnchorClick() {
        this.showCompanyList = !this.showCompanyList;
    }

    displayRequiredValidationMessage(validationControl: string): boolean {
        return this.controls[validationControl]?.hasError('required') &&
            this.controls[validationControl].touched;
    }

    getPostalAddress(place: google.maps.places.PlaceResult): void {
        let address = this.addressValidationService.addressFromPlaceResult(place, this.addressType);
        this.controls.postalAddress.setValue(!address?.addressLine1 ? null : address);
        this.controls.postalAddress.markAllAsTouched();
    }

    getStreetAddress(place: google.maps.places.PlaceResult): void {
        this.controls.streetAddress.setValue(this.addressValidationService.addressFromPlaceResult(place, this.addressType));
    }

    private addressDetailAsAddress(detail: AddressDetail | undefined): Address | undefined {
        if (detail === undefined) {
            return undefined;
        }
        return {
            line1: detail?.name ?? detail?.addressLine1,
            line2: detail?.name?.length ?? 0 > 0 ? detail?.addressLine1 : '',
            city: detail?.suburb,
            postalCode: detail?.postCode,
            state: detail?.state,
            country: detail?.country
        } as Address;
    }
}
